/**
 * WBStorefrontApi - Shared helper for Shopify Storefront GraphQL API
 * Used by AlgoliaMediaManager and other components for batch product fetching
 */

// Verbose per-request GraphQL tracing. Off by default so shops with Tagalys
// disabled stay completely silent - the existing Algolia hover-image path also
// calls WBStorefrontApi.fetch, and this must not add console noise there.
// Active only on Tagalys collection pages (window.wbTagalysCollection, set in
// layout/theme.liquid) or when window.WBStorefrontApiDebug is set from devtools.
function wbSfDebugLog() {
  if (window.wbTagalysCollection || window.WBStorefrontApiDebug) {
    console.log.apply(console, arguments);
  }
}

window.WBStorefrontApi = {
  gid: 'gid://shopify',
  accessToken: '',

  /**
   * Convert resource type and ID to Shopify Global ID format
   * @param {string} resource - Resource type (e.g., 'Product', 'ProductVariant')
   * @param {string|number} id - Numeric ID
   * @returns {string} Global ID (e.g., 'gid://shopify/Product/123')
   */
  toGid: function(resource, id) {
    return this.gid + '/' + resource + '/' + id;
  },

  /**
   * Convert array of IDs to Global IDs
   * @param {string} resource - Resource type
   * @param {Array<string|number>} ids - Array of numeric IDs
   * @returns {Array<string>} Array of Global IDs
   */
  toGids: function(resource, ids) {
    var self = this;
    return ids.map(function(id) { return self.toGid(resource, id); });
  },

  /**
   * Check if value is a Global ID
   * @param {*} value - Value to check
   * @returns {boolean}
   */
  isGid: function(value) {
    return typeof value === 'string' && value.indexOf(this.gid) === 0;
  },

  /**
   * Extract numeric ID from Global ID or return stringified value
   * @param {string|number} value - Global ID or numeric ID
   * @returns {string} Numeric ID as string
   */
  toNumericId: function(value) {
    if (typeof value === 'number') return String(value);
    if (this.isGid(value)) {
      return value.replace(/\D/g, '');
    }
    return String(value);
  },

  /**
   * Storefront API calls from theme code are same-origin and authenticated by the
   * buyer session, so no access token is required. This is only an escape hatch:
   * set WBStorefrontApi.accessToken or window.WBStorefrontApiAccessToken from a
   * snippet if a token ever becomes necessary.
   * @returns {string} Access token, or empty string
   */
  getAccessToken: function() {
    // Set by fetch() once Shopify has rejected the configured token (see there).
    if (this.tokenRejected) return '';
    return this.accessToken || window.WBStorefrontApiAccessToken || '';
  },

  hasAccessToken: function() {
    return !!this.getAccessToken();
  },

  /**
   * Execute GraphQL query against Storefront API
   * @param {string} query - GraphQL query string
   * @param {Object} variables - Query variables
   * @param {Object} [options] - Optional extras. options.signal (AbortSignal)
   *   cancels the underlying fetch - only meaningful to callers that pass it;
   *   existing callers that omit options behave exactly as before.
   * @returns {Promise<Object|null>} Response data on success, null on any
   *   non-abort error (network, HTTP, GraphQL, or malformed response - each
   *   logs its own distinct console.error so the failure class is still
   *   traceable in devtools, even though the resolved value is uniformly
   *   null for backward compatibility with existing callers). Rejects only
   *   for an intentionally aborted request (options.signal aborted), so
   *   callers that opt into cancellation can tell "cancelled" apart from
   *   "failed" - this can only happen for callers that pass options.signal,
   *   so it never changes behavior for callers that don't.
   */
  fetch: function(query, variables, options) {
    var headers = {
      'Accept': 'application/graphql-response+json, application/json',
      'Content-Type': 'application/json'
    };
    var accessToken = this.getAccessToken();
    var init = {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({ query: query, variables: variables })
    };
    // Every caller (Tagalys grid/facets, quick-add color siblings, etc.)
    // names its query (e.g. "query TagalysFilter(...)"), so logging that name
    // here - the single choke point every GraphQL call goes through - traces
    // every request/response in one place instead of needing a log line
    // duplicated at each call site.
    var operationName = (/^\s*(?:query|mutation)\s+(\w+)/.exec(query) || [])[1] || 'anonymous';
    wbSfDebugLog('[GraphQL →]', operationName, variables);
    if (accessToken) {
      headers['X-Shopify-Storefront-Access-Token'] = accessToken;
    }
    if (options && options.signal) {
      init.signal = options.signal;
    }
    return fetch('/api/2025-10/graphql.json', init)
    .then(function(r) {
      // A wrong/revoked token turns every call into a 401 even though the same
      // same-origin request works tokenless - which dropped the Tagalys PLP to
      // its DOM fallback. Retry once without it and stop sending it for the
      // rest of the page, so a bad theme setting can only cost the metafield
      // read, never the grid.
      if ((r.status === 401 || r.status === 403) && accessToken) {
        console.error('Storefront API rejected the "Storefront API access token" theme setting (' + r.status + '); retrying without it.');
        window.WBStorefrontApi.tokenRejected = true;
        return window.WBStorefrontApi.fetch(query, variables, options);
      }
      if (!r.ok) {
        console.error('Storefront API HTTP error:', r.status, r.statusText);
        wbSfDebugLog('[GraphQL ←]', operationName, 'http-error', r.status);
        return null;
      }
      return r.json().then(function(result) {
        // GraphQL allows a response to carry both `data` and `errors` at
        // once (a single field - e.g. one this store's Storefront token
        // lacks a metafield-read scope for - can fail while every other
        // field in the same query still resolved). Discarding the whole
        // response on any error meant one denied field (mainCategories)
        // was silently blanking every product-list/filter/count request
        // that included it, live-verified on ie.womensbest.com: a color
        // filter returned 0 usable data and fell back to the ~24-card DOM
        // scan instead of the real ~130-product result. Still log errors
        // (a real access-scope gap is worth fixing at the source), but
        // only treat the request as failed when there's no data at all.
        if (result.errors) {
          console.error('Storefront API errors:', result.errors);
        }
        if (!result.data) {
          wbSfDebugLog('[GraphQL ←]', operationName, 'no-data', result.errors || null);
          return null;
        }
        wbSfDebugLog('[GraphQL ←]', operationName, 'ok', result.data);
        return result.data;
      }, function(parseError) {
        if (parseError && parseError.name === 'AbortError') {
          wbSfDebugLog('[GraphQL ←]', operationName, 'aborted');
          throw parseError;
        }
        console.error('Storefront API malformed response:', parseError);
        wbSfDebugLog('[GraphQL ←]', operationName, 'malformed');
        return null;
      });
    })
    .catch(function(error) {
      if (error && error.name === 'AbortError') {
        wbSfDebugLog('[GraphQL ←]', operationName, 'aborted');
        throw error;
      }
      console.error('Storefront API fetch error:', error);
      wbSfDebugLog('[GraphQL ←]', operationName, 'fetch-error', error);
      return null;
    });
  }
};
